import { useEffect, useMemo, useState } from 'react';
import posthog from '../posthog';

const STORAGE_KEY = 'theme'; // 'light' | 'dark' | 'system'
const MANUAL_OVERRIDE_KEY = 'theme-manual-override'; // Track if user manually set theme

function getPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // system
    if (getPrefersDark()) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

function getInitialTheme() {
  if (typeof window === 'undefined') return 'system';
  
  // Check if user has manually set a theme (this overrides feature flag)
  const hasManualOverride = localStorage.getItem(MANUAL_OVERRIDE_KEY) === 'true';
  if (hasManualOverride) {
    return localStorage.getItem(STORAGE_KEY) || 'system';
  }
  
  // If no manual override, check feature flag (supports 'light' | 'dark' | 'system')
  const featureFlag = posthog.getFeatureFlag('light-mode');
  if (featureFlag === 'light') {
    return 'light';
  } else if (featureFlag === 'dark') {
    return 'dark';
  } else if (featureFlag === 'control') {
    return 'system';
  }
  
  // Fallback to stored value or system
  return localStorage.getItem(STORAGE_KEY) || 'system';
}

export function useTheme() {
  const [theme, setTheme] = useState(() => getInitialTheme());

  // Listen for PostHog feature flags on initial load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    posthog.onFeatureFlags(function() {
      // Only apply feature flag if user hasn't manually overridden
      const hasManualOverride = localStorage.getItem(MANUAL_OVERRIDE_KEY) === 'true';
      if (!hasManualOverride) {
        const featureFlag = posthog.getFeatureFlag('default-theme');
        if (featureFlag === 'light') {
          setTheme('light');
        } else if (featureFlag === 'dark') {
          setTheme('dark');
        } else if (featureFlag === 'system') {
          setTheme('system');
        }
      }
    });
  }, []);

  // Apply theme and persist preference (system = no storage)
  useEffect(() => {
    applyThemeClass(theme);
    try {
      if (theme === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, theme);
      }
    } catch {}
  }, [theme]);

  // Listen to system changes when in system mode
  useEffect(() => {
    if (typeof window === 'undefined' || theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyThemeClass('system');
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [theme]);

  const isDark = useMemo(() => {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    return getPrefersDark();
  }, [theme]);

  const toggle = () => {
    // Mark that user has manually toggled theme (override feature flag)
    try {
      localStorage.setItem(MANUAL_OVERRIDE_KEY, 'true');
    } catch {}
    setTheme(isDark ? 'light' : 'dark');
  };
  
  const setSystem = () => {
    // Mark that user has manually set to system (override feature flag)
    try {
      localStorage.setItem(MANUAL_OVERRIDE_KEY, 'true');
    } catch {}
    setTheme('system');
  };

  return { theme, setTheme, toggle, setSystem, isDark };
}
